/**
 * Client Invitation Banner Component
 *
 * Displays an invitation banner for clients who have pending mentor invitations.
 * Allows clients to accept or reject mentor invitations.
 */

import React, { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { getUserProfile } from "../../services/userService";
import { updateClientInvitationStatus } from "../../services/mentorClientService";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useTranslation } from "../../hooks/useTranslation";
import "./ClientInvitationBanner.css";

export const ClientInvitationBanner = () => {
  const { currentUser } = useAuth();
  const analytics = useAnalytics();
  const t = useTranslation();
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [dismissedInvitations, setDismissedInvitations] = useState(new Set());
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadInvitations();
    }
  }, [currentUser]);

  const loadInvitations = async () => {
    try {
      const userProfile = await getUserProfile(currentUser.uid);
      if (userProfile && userProfile.managedBy) {
        const invitations = [];

        for (const [mentorId, mentorData] of Object.entries(
          userProfile.managedBy
        )) {
          if (mentorData.status === "pending") {
            invitations.push({
              mentorId,
              ...mentorData,
            });
          }
        }

        setPendingInvitations(invitations);
      }
    } catch (error) {
      console.error("Error loading invitations:", error);
    }
  };

  const handleAccept = async (mentorId) => {
    setProcessing(true);
    try {
      // Track invitation acceptance
      analytics.trackCTAClick({
        action: "client_invitation_accept",
        section: "client_invitation",
        source: "invitation_banner",
      });

      await updateClientInvitationStatus(mentorId, "active");
      await loadInvitations(); // Reload to update status
    } catch (error) {
      console.error("Error accepting invitation:", error);
      alert("Failed to accept invitation. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (mentorId) => {
    setProcessing(true);
    try {
      // Track invitation rejection
      analytics.trackCTAClick({
        action: "client_invitation_reject",
        section: "client_invitation",
        source: "invitation_banner",
      });

      await updateClientInvitationStatus(mentorId, "rejected");
      await loadInvitations(); // Reload to update status
    } catch (error) {
      console.error("Error rejecting invitation:", error);
      alert("Failed to reject invitation. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const handleDismiss = (mentorId) => {
    setDismissedInvitations(new Set([...dismissedInvitations, mentorId]));
  };

  const visibleInvitations = pendingInvitations.filter(
    (inv) => !dismissedInvitations.has(inv.mentorId)
  );

  if (visibleInvitations.length === 0) {
    return null;
  }

  return (
    <div className="client-invitations-container">
      {visibleInvitations.map((invitation) => (
        <div key={invitation.mentorId} className="client-invitation-banner">
          <div className="invitation-content">
            <div className="invitation-icon">📧</div>
            <div className="invitation-text">
              <strong>{t("client.invitation.title")}</strong>
              <p>{t("client.invitation.message")}</p>
            </div>
          </div>
          <div className="invitation-actions">
            <button
              className="btn-accept"
              onClick={() => handleAccept(invitation.mentorId)}
              disabled={processing}
            >
              {t("client.invitation.accept")}
            </button>
            <button
              className="btn-reject"
              onClick={() => handleReject(invitation.mentorId)}
              disabled={processing}
            >
              {t("client.invitation.reject")}
            </button>
            <button
              className="btn-dismiss"
              onClick={() => handleDismiss(invitation.mentorId)}
              disabled={processing}
              title={t("client.invitation.decideLater")}
            >
              ✕
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ClientInvitationBanner;
